import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FoxLogo } from '../components/FoxLogo';
import { ArrowLeft, Building, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast, Toaster } from 'sonner';
export function Login() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [activeTab, setActiveTab] = useState<'customer' | 'business'>('customer');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const navigate = useNavigate();
  const {
    customerLogin,
    customerSignup
  } = useAuth();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      if (mode === 'signup') {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }
        const result = await customerSignup(name, email, password, activeTab, company, phone, address, state, zip);
        if (result.error) {
          setError(result.error);
        } else {
          toast.success('Account created successfully!');
          // Navigate to appropriate dashboard
          navigate(`/dashboard/${activeTab}`);
        }
      } else {
        // Login mode
        const result = await customerLogin(email, password);
        if (result.error) {
          setError(result.error);
        } else if (result.type) {
          // Navigate based on their actual account type from DB
          navigate(`/dashboard/${result.type}`);
        } else {
          // Fallback if type isn't returned
          navigate(`/dashboard/${activeTab}`);
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError(null);
    setPassword('');
    setConfirmPassword('');
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden" data-id="element-2507">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-2508">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-fox-600/10 rounded-full blur-[100px]" data-id="element-2509" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-amber-500/10 rounded-full blur-[100px]" data-id="element-2510" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02]" data-id="element-2511" />
      </div>

      <Toaster theme="dark" position="top-right" data-id="element-2512" />

      <Link to="/" className="absolute top-8 left-8 text-slate-400 hover:text-white flex items-center gap-2 transition-colors z-10" data-id="element-2513">
        <ArrowLeft size={20} data-id="element-2514" />
        <span data-id="element-2515">Back to Home</span>
      </Link>

      <div className="w-full max-w-md relative z-10" data-id="element-2516">
        <div className="text-center mb-8" data-id="element-2517">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 20
        }} className="inline-block mb-4" data-id="element-2518">
            <FoxLogo className="w-16 h-16" data-id="element-2519" />
          </motion.div>
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-2520">
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-slate-400" data-id="element-2521">
            {mode === 'login' ? 'Log in to your Fox Line account' : 'Join Fox Line Hosting today'}
          </p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl" data-id="element-2522">
          {/* Tabs */}
          <div className="flex relative border-b border-slate-800" data-id="element-2523">
            <button onClick={() => setActiveTab('customer')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors z-10 ${activeTab === 'customer' ? 'text-white' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-2524">
              <User size={18} data-id="element-2525" />
              Customer
            </button>
            <button onClick={() => setActiveTab('business')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors z-10 ${activeTab === 'business' ? 'text-white' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-2526">
              <Building size={18} data-id="element-2527" />
              Business
            </button>

            {/* Animated Tab Indicator */}
            <motion.div className="absolute bottom-0 left-0 h-0.5 bg-fox-500 w-1/2" animate={{
            x: activeTab === 'customer' ? 0 : '100%'
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-2528" />
          </div>

          {/* Form Content */}
          <div className="p-8" data-id="element-2529">
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3" data-id="element-2530">
                <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} data-id="element-2531" />
                <p className="text-sm text-red-400" data-id="element-2532">{error}</p>
              </motion.div>}

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2533">
              <AnimatePresence mode="popLayout" data-id="element-2534">
                {mode === 'signup' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="space-y-2" data-id="element-2535">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2536">
                      Full Name
                    </label>
                    <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="John Doe" data-id="element-2537" />
                  </motion.div>}

                {activeTab === 'business' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="space-y-2" data-id="element-2538">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2539">
                      {mode === 'login' ? 'Company ID (Optional)' : 'Company Name'}
                    </label>
                    <input type="text" required={mode === 'signup'} value={company} onChange={e => setCompany(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder={mode === 'login' ? 'e.g. FOX-12345' : 'Acme Corp'} data-id="element-2540" />
                  </motion.div>}

                {mode === 'signup' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="space-y-4" data-id="element-2541">
                    <div className="space-y-2" data-id="element-2542">
                      <label className="text-sm font-medium text-slate-300" data-id="element-2543">
                        Phone Number
                      </label>
                      <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="(555) 123-4567" data-id="element-2544" />
                    </div>
                    <div className="space-y-2" data-id="element-2545">
                      <label className="text-sm font-medium text-slate-300" data-id="element-2546">
                        Street Address
                      </label>
                      <input type="text" required value={address} onChange={e => setAddress(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="123 Main St" data-id="element-2547" />
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-2548">
                      <div className="space-y-2" data-id="element-2549">
                        <label className="text-sm font-medium text-slate-300" data-id="element-2550">
                          State
                        </label>
                        <input type="text" required value={state} onChange={e => setState(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="CA" data-id="element-2551" />
                      </div>
                      <div className="space-y-2" data-id="element-2552">
                        <label className="text-sm font-medium text-slate-300" data-id="element-2553">
                          ZIP Code
                        </label>
                        <input type="text" required value={zip} onChange={e => setZip(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="90210" data-id="element-2554" />
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <div className="space-y-2" data-id="element-2555">
                <label className="text-sm font-medium text-slate-300" data-id="element-2556">
                  {activeTab === 'business' ? 'Work Email' : 'Email Address'}
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="you@example.com" data-id="element-2557" />
              </div>

              <div className="space-y-2" data-id="element-2558">
                <div className="flex items-center justify-between" data-id="element-2559">
                  <label className="text-sm font-medium text-slate-300" data-id="element-2560">
                    Password
                  </label>
                  {mode === 'login' && <a href="#" className="text-xs text-fox-500 hover:text-fox-400" data-id="element-2561">
                      Forgot password?
                    </a>}
                </div>
                <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="••••••••" data-id="element-2562" />
              </div>

              <AnimatePresence mode="popLayout" data-id="element-2563">
                {mode === 'signup' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="space-y-2" data-id="element-2564">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2565">
                      Confirm Password
                    </label>
                    <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="••••••••" data-id="element-2566" />
                  </motion.div>}
              </AnimatePresence>

              {mode === 'login' && <div className="flex items-center" data-id="element-2567">
                  <input type="checkbox" id="remember" className="w-4 h-4 rounded border-slate-700 bg-slate-950 text-fox-600 focus:ring-fox-500 focus:ring-offset-slate-900" data-id="element-2568" />
                  <label htmlFor="remember" className="ml-2 text-sm text-slate-400" data-id="element-2569">
                    Remember me for 30 days
                  </label>
                </div>}

              <button type="submit" disabled={isLoading} className="w-full bg-fox-600 hover:bg-fox-500 text-white font-medium py-3 rounded-lg transition-all shadow-[0_0_15px_rgba(234,88,12,0.2)] hover:shadow-[0_0_25px_rgba(234,88,12,0.4)] mt-2 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-2570">
                {isLoading && <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2571" />}
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-400" data-id="element-2572">
              {mode === 'login' ? <>
                  Don't have an account?{' '}
                  <button onClick={toggleMode} type="button" className="text-fox-500 hover:text-fox-400 font-medium" data-id="element-2573">
                    Sign up
                  </button>
                </> : <>
                  Already have an account?{' '}
                  <button onClick={toggleMode} type="button" className="text-fox-500 hover:text-fox-400 font-medium" data-id="element-2574">
                    Sign in
                  </button>
                </>}
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}